import {
  Avatar,
  Badge,
  Divider,
  Icon,
  IconNames,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens/index';
import * as Popover from '@radix-ui/react-popover';
import { useHasAccess } from 'lib-shared-access-control';
import type { LoadDescriptionT, Presence } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import capitalize from 'lodash/capitalize';
import noop from 'lodash/noop';
import { ComponentProps, Fragment, ReactNode } from 'react';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import {
  PresenceDropdownItem,
  PresenceToggle,
} from '../../profile/components/PresenceDropdownItem';

import PresenceOverlayChannels from './PresenceOverlayChannels';
import styles from './presenceOverlayPopover.module.scss';

type PresenceOverlayPopoverProps = {
  user: User;
  presence: Presence;
  acwConversationNode: JSX.Element | null;
  lastSeen: string | null;
  onInternalCallClickHandler: () => void;
  isSimultaneousRingLoading: boolean;
  presenceSetOthersSimultaneousRinging: (args: any) => Promise<void>;
  isOwnAgent: boolean;
  internalBtnText: string;
  disabledCall: string;
  showUserModal: (payload: { user: User }) => void;
  conversationsByChannel: Partial<Record<IconNames, LoadDescriptionT[]>>;
  handlePresenceLinkClick: (csid: number) => void;
  isFavorite: boolean;
  addToFavorites: (userId: string) => void;
  removeFromFavorites: (userId: string) => void;
  children: ReactNode;
};

export const PresenceOverlayPopover = ({
  user,
  presence,
  acwConversationNode,
  lastSeen,
  onInternalCallClickHandler,
  isSimultaneousRingLoading,
  presenceSetOthersSimultaneousRinging,
  isOwnAgent,
  internalBtnText,
  disabledCall,
  showUserModal,
  conversationsByChannel,
  handlePresenceLinkClick,
  isFavorite,
  addToFavorites,
  removeFromFavorites,
  children,
}: PresenceOverlayPopoverProps): JSX.Element => {
  const hasConversationsAssigned = Object.keys(conversationsByChannel).some(
    (channel) => conversationsByChannel[channel].length > 0,
  );
  const featureFlags = useFeatureFlag(['phone-channel']);

  const hasOrganizationMemberAccess = useHasAccess('organization.member');
  const hasConversationReplyAccess = useHasAccess('conversation.reply');

  const hasChannelPhone = featureFlags.phoneChannel;

  const presenceBadgeMap: Record<
    Presence['status'],
    ComponentProps<typeof Badge>['variant']
  > = {
    working: 'success',
    away: 'warning',
    offline: 'secondary',
  };

  const hasPhone = !!user.phone_number && user.phone_number.length > 0;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{children}</Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={styles.dropdown} side="right">
          <div className={styles.header}>
            <Avatar user={user} />

            <Text.Paragraph className={styles.name} as="span" variant="2">
              {user.name !== undefined && user.name !== null
                ? user.name.replace(/ .*/, '')
                : ''}
            </Text.Paragraph>

            {presence.status && (
              <Badge
                variant={presenceBadgeMap[presence.status] ?? 'secondary'}
                size="medium"
              >
                {capitalize(presence.status)}
              </Badge>
            )}
          </div>

          <PresenceOverlayChannels presence={presence} />

          <Divider />

          <div>
            {[acwConversationNode, lastSeen]
              .filter(Boolean)
              .map((item, index) => (
                <Fragment key={index}>
                  <Text.Paragraph
                    key={index}
                    variant="2"
                    as="div"
                    color="tertiary"
                    padding="200 0"
                    align="center"
                  >
                    {item}
                  </Text.Paragraph>

                  <Divider />
                </Fragment>
              ))}

            {hasChannelPhone && (
              <>
                {hasConversationReplyAccess && hasOrganizationMemberAccess && (
                  <PresenceDropdownItem
                    icon="simultaneous"
                    text="Simultaneous ring"
                    tooltipText={
                      hasPhone
                        ? `Toggle simultaneous ringing for ${
                            user && user.name ? user.name : 'agent'
                          }`
                        : 'Simultaneous ringing is turned off (phone number needed)'
                    }
                    disabled={!hasPhone}
                  >
                    <PresenceToggle
                      id="shouldOpenSimultaneous"
                      disabled={hasPhone && isSimultaneousRingLoading}
                      active={!!presence.presence_status.simultaneous_ringing}
                      onToggle={
                        hasPhone && !isSimultaneousRingLoading
                          ? () => {
                              presenceSetOthersSimultaneousRinging({
                                userId: user.id,
                                enabled:
                                  !presence.presence_status
                                    .simultaneous_ringing,
                              });
                            }
                          : noop
                      }
                    />
                  </PresenceDropdownItem>
                )}

                {hasConversationReplyAccess && !isOwnAgent && (
                  <PresenceDropdownItem
                    icon="phone"
                    text={internalBtnText}
                    tooltipText={`Make internal call to ${user.name}`}
                    disabled={disabledCall !== ''}
                    onClick={onInternalCallClickHandler}
                  />
                )}
              </>
            )}

            {(isOwnAgent || hasOrganizationMemberAccess) && (
              <PresenceDropdownItem
                icon="pen"
                text="Edit agent"
                onClick={() => {
                  showUserModal({ user });
                }}
              />
            )}

            <PresenceDropdownItem
              icon={
                <Icon
                  name={isFavorite ? 'star-full' : 'star'}
                  color={isFavorite ? colors.orange[300] : colors.neutral[400]}
                />
              }
              text={isFavorite ? 'Unfavorite agent' : 'Favorite agent'}
              onClick={() =>
                isFavorite
                  ? removeFromFavorites(user.id)
                  : addToFavorites(user.id)
              }
            />

            <Divider />

            {!hasConversationsAssigned ? (
              <PresenceDropdownItem
                icon="chat-slash"
                text="No conversations here"
                disabled={true}
              />
            ) : (
              (Object.keys(conversationsByChannel) as IconNames[]).map(
                (icon) => {
                  return conversationsByChannel[icon]?.map((item, index) => (
                    <PresenceDropdownItem
                      key={index}
                      icon={icon}
                      text={`Open #${item.csid}`}
                      onClick={() => handlePresenceLinkClick(item.csid)}
                    />
                  ));
                },
              )
            )}
          </div>
          <Popover.Arrow style={{ fill: 'white' }} width={16} height={8} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
